import { DeliveryMethod, RequestForQuoteStep, useCustomerContactQuery } from '@/dealers/_gen/gql'
import useSession from '@/dealers/hooks/useSession'
import { MultiButtons } from '@/gf/components/MultiButton'
import useMsgs from '@/gf/hooks/useMsgs'
import { completesCreateQuote, initiatesCreateQuote } from '@/gf/modules/GrammarEvents'
import { cleanGraphQlInput } from '@/gf/modules/GraphQL'
import MoneyM from '@/gf/modules/Money'
import MachineInformation from '../MachineInformation'
import QuoteBuilder from '../QuoteBuilder'
import StepNote from '../StepNote'
import { viewOriginalRequestAction } from '../ViewOriginalRequest'
import { useRfqContext } from '../context'
import QuoteUploadBar from '../QuoteUpload/QuoteUploadBar'
import Comments from '../Comments'
import { useMemo } from 'react'
import { deletePersistedForm } from '../QuoteBuilder/useForm'
import Id from '@/gf/modules/Id'
import { DocumentUploadContextProps, FormItem } from '../types'

const CreateQuote = ({
  quoteUpload,
  onReadyForPickup,
  onMarkPickedUp,
  onCreateShipping,
  onAcceptQuote,
  onFormItemsChange,
  ...props
}: {
  onQuoteSuccess: () => void
  quoteUpload: DocumentUploadContextProps
  onReadyForPickup: () => void
  onMarkPickedUp: () => void
  onCreateShipping: () => void
  onAcceptQuote: () => void
  onFormItemsChange?: (items: FormItem[]) => void
}) => {
  const { rfq, refetch, invoiceSent } = useRfqContext()
  const { store } = useSession()
  const [_, msgr] = useMsgs()
  const { data } = useCustomerContactQuery({
    variables: { customerContactId: rfq.user?.id ?? '' },
    skip: !rfq.user,
  })

  const storageKey = useMemo(() => Id.shorten(rfq.id), [rfq.id])

  // Prefer context lineItems (from new Document model) over legacy rfq.quoteUpload
  const hasContextLineItems = quoteUpload.lineItems.length > 0

  // Clear localStorage when AI corrections are applied so form reinitializes with new values
  // Context lineItems are updated on every extraction/correction
  if (hasContextLineItems) {
    deletePersistedForm(storageKey)
  }

  if (rfq.quote || rfq.step !== RequestForQuoteStep.Inbound || rfq.closed) return null

  const customerAddress = rfq.shippingAddress ?? data?.customerContact.account.address ?? null

  const onError = (message: string, needsReload: boolean) => {
    if (needsReload)
      msgr.addWithAction(message, { text: 'Reload the page', callback: refetch }, 'negative')
    else msgr.add(message, 'negative')
  }

  // Priority: 1) context line items (new Document model), 2) legacy rfq.quoteUpload, 3) part requests
  const items = hasContextLineItems
    ? quoteUpload.lineItems
    : rfq.quoteUpload
      ? rfq.quoteUpload.lineItems.map((li) => ({
          id: null,
          partNumber: li.partNumber,
          // Legacy QuoteUpload doesn't expose alts; populated only by the
          // post-extraction `extractionToLineItems` path in Sales Hub.
          altPartNumbers: [],
          description: li.description,
          unitPrice: li.unitPrice ? MoneyM.toDecimal(li.unitPrice) : null,
          quantity: li.quantity,
          inStock: true,
          externalId: li.externalId,
          taskNumber: li.taskNumber,
          suggestion: li.suggestion,
          availabilityDate: null,
          rfqPartId: null,
          rfqPartNumber: null,
        }))
      : rfq.partRequests.map((pr) => ({
          id: null,
          partNumber: pr.mpn,
          altPartNumbers: [],
          description: pr.description,
          unitPrice: null,
          // Use remainingQuantity when available (after partial inventory fulfillment)
          // to show vendors only the quantity still needed. Floor at 1 to prevent
          // negative or zero quantities when fulfilled exceeds the original request.
          quantity: Math.max(pr.remainingQuantity ?? pr.quantity, 1),
          inStock: true,
          externalId: pr.externalId,
          taskNumber: pr.taskNumber,
          suggestion: pr.suggestion,
          availabilityDate: null,
          rfqPartId: pr.id,
          rfqPartNumber: pr.mpn,
        }))

  const initialValues = {
    customerContactId: null,
    // Prefer extracted value when present. `null` from the extractor means
    // "not on the doc" — keep the form default rather than zeroing out.
    shippingCost:
      hasContextLineItems && quoteUpload.shippingCost !== null ? quoteUpload.shippingCost : null,
    taxCost: hasContextLineItems && quoteUpload.taxCost !== null ? quoteUpload.taxCost : null,
    customerDiscount: rfq.user?.organization?.discount ?? null,
    quoteNumber: null,
    deliveryMethod: rfq.requestDetails?.deliveryMethod ?? DeliveryMethod.Shipping,
    items,
    pickupAddress: store.address ? cleanGraphQlInput(store.address) : null,
    shippingAddress: customerAddress ? cleanGraphQlInput(customerAddress) : null,
    comments: null,
    neededByDate: null,
    urgency: null,
  }

  const onQuoteSuccess = (storeOrderId: string) => {
    props.onQuoteSuccess()
    completesCreateQuote({ rfqId: rfq.id, storeOrderId })
  }

  return (
    <QuoteBuilder
      key={hasContextLineItems ? `document-v${quoteUpload.lineItemsVersion}` : 'initial'}
      rfqId={rfq.id}
      initialValues={initialValues}
      images={rfq.images.map((i) => i.url)}
      machineDetailsSection={
        <>
          <MachineInformation
            reqMachine={rfq.machines[0] ?? null}
            className="px-4"
            hideUnitNumber={rfq.user?.organization?.hideUnitNumberFromVendors ?? false}
          />

          {rfq.partsRequest && <Comments partsRequest={rfq.partsRequest} />}
        </>
      }
      onSuccess={onQuoteSuccess}
      actions={
        <QuoteUploadBar
          onError={quoteUpload.onError}
          onUploadComplete={quoteUpload.onUploadComplete}
          onUploadStarted={quoteUpload.onUploadStarted}
          rfqId={rfq.id}
          cantParticipateLinkTo="cant-participate"
          hasQuoteUpload={rfq.hasQuoteUpload}
        />
      }
      onError={onError}
      sideNote={
        <StepNote
          rfq={rfq}
          onReadyForPickup={onReadyForPickup}
          onMarkPickedUp={onMarkPickedUp}
          onCreateShipping={onCreateShipping}
          onAcceptQuote={onAcceptQuote}
          invoiceSent={invoiceSent}
        />
      }
      secondaryActions={
        <MultiButtons className="!shadow-sm" items={[viewOriginalRequestAction(rfq)]} />
      }
      showSalesTax={rfq.requestDetails !== null}
      customerFeeRate={!rfq.requestDetails ? store.convenienceFeeRate : null}
      onTouched={() => initiatesCreateQuote({ rfqId: rfq.id })}
      showWhenCustomerDiscountAutoFilled
      onItemsChange={onFormItemsChange}
      customerEmail={rfq.user ? rfq.user.email : rfq.emailAddress}
      customerPhoneNumber={rfq.user ? rfq.user.phoneNumber : rfq.phoneNumber}
    />
  )
}

export default CreateQuote
